import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CssBaseline,
  Divider,
  Button,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const drawerWidth = 240;

const MainLayout = ({ children, activeTab, setActiveTab, onLogout }) => {
  const navigate = useNavigate();
  const { activeBranch, isCompanyAdmin, tenant, user } = useAuth();
  const [open, setOpen] = useState(true);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const masters = ['साहित्य मास्टर', 'ग्राहक मास्टर', 'व्यापारी मास्टर', 'खाते मास्टर'];
  const vyavahar = ['ग्राहक व्यवहार (Rental)', 'व्यापारी व्यवहार', 'खरेदी व्यवहार', 'जमा-खर्च / रोजमेळ'];
  const reports = ['आउटस्टँडिंग साहित्य आणि अमाऊंट येणे'];
  const utilities = ['डेटा Repair Tool'];

  const handleMenuClick = (text) => {
    if (setActiveTab) {
      setActiveTab(text);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* Top Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#C8E6C9', // Light green
          color: '#1B5E20', // Dark green text
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Branch Indicator — center of AppBar */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            {activeBranch ? (
              <Chip
                icon={<LocationOnIcon sx={{ color: '#1B5E20 !important', fontSize: '18px !important' }} />}
                label={activeBranch.name}
                size="medium"
                sx={{
                  bgcolor: 'rgba(27,94,32,0.1)',
                  color: '#1B5E20',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  border: '1px solid rgba(27,94,32,0.3)',
                }}
              />
            ) : (
              <Chip
                icon={<AccountBalanceIcon sx={{ color: '#1a237e !important', fontSize: '18px !important' }} />}
                label="Head Office — सर्व Branches"
                size="medium"
                sx={{
                  bgcolor: 'rgba(26,35,126,0.08)',
                  color: '#1a237e',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  border: '1px solid rgba(26,35,126,0.25)',
                }}
              />
            )}
            {isCompanyAdmin && (
              <IconButton
                size="small"
                title="Branch बदला"
                onClick={() => navigate('/select-branch')}
                sx={{ color: '#1B5E20', '&:hover': { bgcolor: 'rgba(27,94,32,0.1)' } }}
              >
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Box sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                Rental Book
              </Typography>
              <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold' }}>
                User : {user?.name || JSON.parse(localStorage.getItem('csm_user') || '{}')?.name || 'User'}
              </Typography>
            </Box>
            <Button
              variant="outlined" color="error" size="small"
              onClick={onLogout}
              startIcon={<PowerSettingsNewIcon />}
              sx={{ borderRadius: 2, ml: 2, borderColor: '#d32f2f', color: '#d32f2f' }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#0FA958', // Green matching design
            color: 'white',
            borderRight: 'none',
          },
        }}
      >
        <Toolbar /> {/* Creates spacing for the AppBar */}
        <Box sx={{ overflow: 'auto', p: 2 }}>
          {/* Dashboard Section */}
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleMenuClick('डॅशबोर्ड')}
                sx={{
                  pl: 3,
                  py: 1,
                  backgroundColor: activeTab === 'डॅशबोर्ड' ? 'rgba(255,255,255,0.2)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                <ListItemText primary="डॅशबोर्ड" primaryTypographyProps={{ fontSize: '1rem', fontWeight: 'bold' }} />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider sx={{ my: 1, backgroundColor: 'rgba(255,255,255,0.2)' }} />

          {/* Masters Section */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>
            मास्टर्स
          </Typography>
          <List disablePadding>
            {masters.map((text) => (
              <ListItem key={text} disablePadding>
                <ListItemButton
                  onClick={() => handleMenuClick(text)}
                  sx={{
                    pl: 3,
                    py: 0.5,
                    backgroundColor: activeTab === text ? 'rgba(255,255,255,0.2)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Box sx={{ my: 3 }} />

          {/* Vyavahar Section */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            व्यवहार
          </Typography>
          <List disablePadding>
            {vyavahar.map((text) => (
              <ListItem key={text} disablePadding>
                <ListItemButton
                  onClick={() => handleMenuClick(text)}
                  sx={{
                    pl: 3,
                    py: 0.5,
                    backgroundColor: activeTab === text ? 'rgba(255,255,255,0.2)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Box sx={{ my: 3 }} />

          {/* Reports Section */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            रिपोर्टस्
          </Typography>
          <List disablePadding>
            {reports.map((text) => (
              <ListItem key={text} disablePadding>
                <ListItemButton
                  onClick={() => handleMenuClick(text)}
                  sx={{
                    pl: 3,
                    py: 0.5,
                    backgroundColor: activeTab === text ? 'rgba(255,255,255,0.2)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Box sx={{ my: 2 }} />

          {/* Utilities Section */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'rgba(255,255,255,0.85)' }}>
            🔧 Utilities
          </Typography>
          <List disablePadding>
            {utilities.map((text) => (
              <ListItem key={text} disablePadding>
                <ListItemButton
                  onClick={() => handleMenuClick(text)}
                  sx={{
                    pl: 3,
                    py: 0.5,
                    backgroundColor: activeTab === text ? 'rgba(255,255,255,0.2)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: '#F5F5F5',
          minHeight: '100vh',
          transition: 'margin 0.3s',
          marginLeft: open ? 0 : `-${drawerWidth}px`,
        }}
      >
        <Toolbar /> {/* Spacer for fixed AppBar */}
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
